import styles from "./MaterialActions.module.scss";
import { Action } from "./useMaterialActions";
import clsx from "clsx";
import { CourseMaterial } from "./coursesLoader.ts";
import useVirtualStore from "../../common/virtual-store/useVirtualStore.tsx";
import { useEffect, useState } from "react";
import LoaderButton from "common/core-ui/LoaderButton.tsx";
import { ProductUnavailableMessage } from "../../common/product/mini-pdp/ProductUnavailable.tsx";
import useMaterialOptInOutStatus from "./useMaterialOptInOutStatus.tsx";
import MaterialOptInOutStatus from "./MaterialOptInOutStatus.tsx";
import useStudentChoice from "./useStudentChoice.tsx";
import { logEvent } from "common/api/monitoring/index.ts";
import MiniPdp from "routes/home/common/product/mini-pdp/MiniPdp.tsx";
import useMaterialOptDeadline from "./useMaterialOptDeadline.tsx";

const HIDDEN_ACTIONS = [
  "view_order_history",
  "view_shipping",
  "update_shipping",
  "opt_in",
  "opt_out",
  "view-details",
];
const OUTLINED_CATEGORIES = ["illFavoredPurchase", "info", "details"];

export function MaterialAction({
  action,
  className,
  isFocusTarget,
}: {
  action: Action;
  className?: string;
  isFocusTarget?: boolean;
}) {
  const [loading, setLoading] = useState(false);
  const { label, icon, href, onClick, to } = action;
  const buttonClasses =
    OUTLINED_CATEGORIES.includes(action.category) &&
    "vst-button-variant-outline";

  return (
    <LoaderButton
      loading={loading}
      aria-label={action.ariaLabel}
      className={clsx("vst-button-pill", className, buttonClasses)}
      href={href}
      onClick={() => {
        if (onClick) {
          const result = onClick();
          if (result) {
            setLoading(true);
            void result.finally(() => setLoading(false));
          }
        }
      }}
      to={to}
      {...(isFocusTarget ? { "data-focus-dest": "" } : {})}
    >
      <div className={clsx(styles.buttonInner)}>
        {icon}
        {label}
      </div>
    </LoaderButton>
  );
}

export default function MaterialActions({
  material,
  actions,
}: {
  material: CourseMaterial;
  actions: Action[];
}) {
  const showActions = actions.filter(
    (action) => !HIDDEN_ACTIONS.includes(action.type ?? "")
  );
  const { isVirtualStore } = useVirtualStore();
  const { isOptInOutProcessing, optInOutStatus } =
    useMaterialOptInOutStatus(material);
  const hasCartAction = !!showActions.find(
    (action) => action.meta === "mini-pdp"
  );
  const hasOptInAction = !!showActions.find(
    (action) => action.type === "opt_in"
  );
  const { isMaterialUnavailable, isMaterialOutOfStock } =
    useStudentChoice(material);
  const { optWindowOpen } = useMaterialOptDeadline(material);
  const accessOverrideClass = optWindowOpen && "vst-button-variant-outline";

  useEffect(() => {
    if (isVirtualStore && hasCartAction && isMaterialOutOfStock) {
      // Log any materials that are shown as unavailable
      logEvent("my_courses_product_out_of_stock", {
        errorMessage:
          "Variants were returned but no variants were available in stock",
        host: window.location.host,
        isbn: material.isbn,
        vbid: material.vbid,
        requiredCode: material.requiredCode ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVirtualStore, material.isbn]);

  if (
    isOptInOutProcessing &&
    optInOutStatus !== "none" &&
    optInOutStatus !== "opting_out"
  ) {
    // opt in and out and revalidation is slow. If the post could supply updated buttons
    // we could do optimistic UI but currently we can't, this is an interim solution
    // "opting_out" state covered by dialog
    return (
      <div className={styles.actions}>
        <MaterialOptInOutStatus material={material} />
      </div>
    );
  }

  return (
    <div className={styles.actions}>
      {isVirtualStore &&
      hasCartAction &&
      isMaterialUnavailable &&
      !hasOptInAction ? (
        <div className={styles.unavailableContainer}>
          <div className={clsx(styles.unavailable)}>
            <ProductUnavailableMessage />
          </div>
        </div>
      ) : (
        showActions.map((action) => {
          if (isVirtualStore && action.meta === "mini-pdp") {
            return (
              <MiniPdp
                key={action.key}
                isbnTitle={material.title}
                addCaption={action.label}
              />
            );
          }
          return (
            <MaterialAction
              key={action.key}
              action={action}
              className={clsx(
                styles.action,
                action.category === "access" && accessOverrideClass
              )}
              isFocusTarget={action.key === "store_view_in_cart"}
            />
          );
        })
      )}
    </div>
  );
}
